import React from "react";
import { registerOverlay } from "../overlays";
import WaitlistForm from "./WaitlistForm";
import { useLang } from "../i18n";
import { emphasise } from "../emphasis";
import { openAnalyticsPreferences } from "../analytics";

function StoreBadge({ testid, top, bottom }) {
  return (
    <button
      data-testid={testid}
      className="flex items-center gap-3 h-14 rounded-[14px] px-5 transition-colors border"
      style={{ background: "var(--bb-surface-2)", borderColor: "var(--bb-border)", color: "var(--bb-ink)" }}
    >
      <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
        <circle cx="12" cy="12" r="10" opacity="0.15" />
        <path d="M8 5v14l11-7z" />
      </svg>
      <span className="text-left leading-tight">
        <span className="block ff-mono text-[9px] uppercase tracking-[0.18em]" style={{ opacity: 0.6 }}>{top}</span>
        <span className="block ff-sans text-[15px] font-medium">{bottom}</span>
      </span>
    </button>
  );
}

export default function FooterOverlay({ isPostLaunch, count, onJoined }) {
  const { t } = useLang();
  const f = t.footer;
  return (
    <section
      ref={(el) => registerOverlay("footer", el)}
      className="fixed inset-0 z-10 flex items-center"
      style={{ willChange: "opacity, transform", opacity: 0 }}
    >
      <div className="mx-auto w-full max-w-[1200px] px-6 lg:px-10">
        <div className="max-w-[620px] mx-auto text-center">
          {/* Keyed to a transparent PNG at build time — the original had an
              opaque black field, and blend modes can't fix that here because
              the section's inline opacity creates a stacking context. */}
          <img
            src="/bb-mark.png"
            alt="BriefBref"
            width={72}
            height={72}
            className="bb-closing-mark mx-auto mb-6 block"
            style={{ width: 72, height: 72 }}
          />
          <p className="ff-mono text-xs uppercase tracking-[0.18em]" style={{ color: "var(--bb-red)" }}>
            {f.kicker}
          </p>
          {/* The close. ff-head at display scale — this is the line that has to
              earn the signup, so it gets the largest type in the section. */}
          <p
            data-testid="footer-signoff"
            className="mt-4 mx-auto ff-head text-4xl sm:text-[44px] leading-[1.12] tracking-[-0.025em]"
            style={{ color: "var(--bb-ink)" }}
          >
            {f.signoffLines.map((line, i) => (
              <span key={i} className="block">
                {emphasise(line, f.signoffEm[i])}
              </span>
            ))}
          </p>

          {/* Perks read as a checklist, not prose: ticks carry the "you get
              this" signal so the text itself can stay white and quiet. Red is
              reserved for the display lines above — two accent colours
              competing in one block was the problem with the previous pass. */}
          <p className="mt-7 ff-mono text-[11px] uppercase tracking-[0.18em]" style={{ color: "var(--bb-ink-faint)" }}>
            {f.unlockLead}
          </p>
          <ul
            data-testid="footer-unlock"
            className="mt-4 inline-flex flex-col gap-2.5 text-left list-none"
          >
            {f.unlockItems.map((item, i) => (
              <li key={i} className="flex items-center gap-3 ff-serif text-[18px] sm:text-[20px] leading-[1.35]" style={{ color: "var(--bb-ink)" }}>
                <svg width="17" height="17" viewBox="0 0 20 20" fill="none" aria-hidden="true" style={{ flexShrink: 0 }}>
                  <circle cx="10" cy="10" r="9" fill="var(--bb-green)" opacity="0.14" />
                  <path
                    d="M5.8 10.3l2.7 2.7 5.7-5.9"
                    stroke="var(--bb-green)"
                    strokeWidth="1.9"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                {item}
              </li>
            ))}
          </ul>

          <div className="bb-interactive flex flex-col items-center">
            {isPostLaunch ? (
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <StoreBadge testid="badge-appstore" top={f.badgeAppTop} bottom={f.badgeAppBottom} />
                <StoreBadge testid="badge-googleplay" top={f.badgePlayTop} bottom={f.badgePlayBottom} />
              </div>
            ) : (
              <WaitlistForm onJoined={onJoined} />
            )}
            {!isPostLaunch && count != null && count > 0 && (
              <p data-testid="waitlist-count" className="mt-4 ff-mono text-xs uppercase tracking-[0.18em]" style={{ color: "var(--bb-ink-faint)" }}>
                {f.count(count)}
              </p>
            )}
          </div>

          {/* Origin sits under the form as quiet credibility. It used to be the
              headline, but at the point of signing up the big type has to
              carry the reason to join, not who built it. */}
          <p
            data-testid="footer-origin"
            className="mt-9 ff-serif text-[15px] sm:text-[16px] leading-[1.5]"
            style={{ color: "var(--bb-ink-faint)" }}
          >
            {emphasise(f.origin, f.originEm)}
          </p>

          {/* These were plain text before — the words "Privacy" and "Terms"
              looked like links and did nothing. Both pages already exist on
              briefbref.ca, in both languages. */}
          <p
            className="bb-interactive mt-12 ff-mono text-xs uppercase tracking-[0.18em] flex flex-wrap items-center justify-center gap-x-3 gap-y-2"
            style={{ color: "var(--bb-ink-faint)" }}
          >
            <span>{f.legal.copyright}</span>
            <span aria-hidden="true">·</span>
            <a
              data-testid="footer-privacy"
              href="https://briefbref.ca/privacy"
              target="_blank"
              rel="noreferrer"
              className="bb-underline"
              style={{ color: "inherit", textDecoration: "none" }}
            >
              {f.legal.privacy}
            </a>
            <span aria-hidden="true">·</span>
            <a
              data-testid="footer-terms"
              href="https://briefbref.ca/terms"
              target="_blank"
              rel="noreferrer"
              className="bb-underline"
              style={{ color: "inherit", textDecoration: "none" }}
            >
              {f.legal.terms}
            </a>
            <span aria-hidden="true">·</span>
            <button
              type="button"
              data-testid="footer-analytics-preferences"
              onClick={openAnalyticsPreferences}
              className="bb-underline"
              style={{ color: "inherit", background: "transparent", border: 0, padding: 0, cursor: "pointer" }}
            >
              {f.legal.analytics}
            </button>
            <span aria-hidden="true">·</span>
            <a
              data-testid="footer-contact"
              href="mailto:hello@briefbref.ca"
              className="bb-underline"
              style={{ color: "inherit", textDecoration: "none" }}
            >
              {f.legal.contact}
            </a>
          </p>
        </div>
      </div>
    </section>
  );
}
